import { Link, NavLink } from "react-router-dom";
import { BRAND } from "@/config/brand";
import type { AppNavGroup, AppUser } from "@/config/appNav";
import { cn } from "@/utils/cn";

interface AppSidebarProps {
  groups: AppNavGroup[];
  user: AppUser;
  planLabel?: string;
  planHint?: string;
  planTo?: string;
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function AppSidebar({
  groups,
  user,
  planLabel,
  planHint,
  planTo,
  onNavigate,
  collapsed = false,
  onToggleCollapse,
}: AppSidebarProps) {
  return (
    <div className="relative flex h-full flex-col bg-foreground-950">
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Agrandir le menu" : "Réduire le menu"}
          title={collapsed ? "Agrandir le menu" : "Réduire le menu"}
          className="absolute -right-3 top-20 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-foreground-800 bg-foreground-900 text-foreground-200 transition-colors hover:bg-primary-500 hover:text-background-50 cursor-pointer"
        >
          <i
            className={cn(
              "text-base transition-transform duration-300",
              collapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"
            )}
            aria-hidden="true"
          />
        </button>
      )}

      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-foreground-900 transition-all duration-300",
          collapsed ? "justify-center px-2" : "px-5"
        )}
      >
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500 font-heading text-sm font-bold text-background-50">
            {BRAND.initials}
          </span>
          {!collapsed && (
            <span className="whitespace-nowrap font-heading text-base font-semibold text-foreground-50">
              {BRAND.name}
            </span>
          )}
        </Link>
      </div>

      <nav
        className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}
        aria-label="Navigation de l'espace"
      >
        {groups.map((group, groupIndex) => (
          <div
            key={group.title ?? `group-${groupIndex}`}
            className={cn(groupIndex > 0 && (collapsed ? "mt-3" : "mt-5"))}
          >
            {group.title && !collapsed && (
              <p className="px-3 pb-2 text-[11px] font-label font-semibold uppercase tracking-wide text-foreground-500">
                {group.title}
              </p>
            )}
            {group.title && collapsed && groupIndex > 0 && (
              <div className="mx-2 mb-2 border-t border-foreground-900" aria-hidden="true" />
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to.split("/").filter(Boolean).length === 1}
                    onClick={onNavigate}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center rounded-md py-2.5 text-sm font-label font-medium transition-colors cursor-pointer",
                        collapsed ? "justify-center px-2" : "gap-3 px-3",
                        isActive
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-200 hover:bg-foreground-900 hover:text-foreground-50"
                      )
                    }
                  >
                    <i className={cn(item.icon, "shrink-0 text-lg")} aria-hidden="true" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {planLabel && planTo && !collapsed && (
        <div className="shrink-0 px-3 pb-2">
          <Link
            to={planTo}
            onClick={onNavigate}
            className="block rounded-lg border border-foreground-800 bg-foreground-900 p-3.5 transition-colors hover:border-primary-500"
          >
            <p className="text-[11px] font-label font-medium uppercase tracking-wide text-foreground-500">
              Formule actuelle
            </p>
            <p className="mt-0.5 text-sm font-heading font-semibold text-foreground-50">{planLabel}</p>
            {planHint && <p className="mt-0.5 text-xs text-foreground-400">{planHint}</p>}
            <span className="mt-2 inline-flex items-center gap-0.5 text-xs font-label font-medium text-primary-300">
              Gérer mon abonnement
              <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
            </span>
          </Link>
        </div>
      )}

      <div className={cn("shrink-0 border-t border-foreground-900", collapsed ? "p-2" : "p-3")}>
        <div
          className={cn(
            "flex items-center rounded-md py-2",
            collapsed ? "justify-center px-0" : "gap-3 px-2"
          )}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 font-label text-xs font-semibold text-background-50">
            {user.initials}
          </span>
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-label font-medium text-foreground-50">{user.name}</p>
              <p className="truncate text-xs text-foreground-400">{user.role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}